import { Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ApiService } from '../../services/api.service';
import { UtilitiesService } from '../../services/utilities.service';

interface ChartBar {
  label: string;
  amount: number;
  count: number;
  height: number;
}

@Component({
  selector: 'app-graph-report',
  templateUrl: './graph-report.component.html',
  styleUrls: ['./graph-report.component.css'],
  standalone: false
})
export class GraphReportComponent implements OnInit {
  token: any;
  monthYear = '';
  items: any[] = [];
  filteredItems: any[] = [];

  totalAmount = 0;
  invoiceCount = 0;
  averageAmount = 0;

  dailyBars: ChartBar[] = [];
  typeBars: ChartBar[] = [];

  constructor(public utility: UtilitiesService, private api: ApiService) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
    this.monthYear = this.toMonthValue(new Date());
  }

  ngOnInit(): void {
    this.getItems();
  }

  getHttpParams(page: number, perPage: number) {
    let params = new HttpParams();
    params = params.append('page', page.toString());
    params = params.append('per_page', perPage.toString());
    params = params.append('sort', 'date');
    params = params.append('sort_order', 'asc');
    return params;
  }

  getItems(page = 1, accumulated: any[] = []) {
    if (page === 1) {
      this.utility.loading = true;
    }

    const perPage = 5000;
    this.api.get('payments/', this.token, { params: this.getHttpParams(page, perPage) }).subscribe(
      async data => {
        const objects: any = data;
        const payments = objects.payments || [];
        const all = accumulated.concat(payments);
        const total = objects['filters']?.['total_results'] ?? all.length;

        if (all.length < total && payments.length > 0) {
          this.getItems(page + 1, all);
          return;
        }

        this.items = all;
        this.buildCharts();
        this.utility.loading = false;
      },
      async error => {
        this.utility.loading = false;
      }
    );
  }

  loadReport() {
    this.buildCharts();
  }

  private buildCharts() {
    this.filteredItems = this.items.filter(item => this.matchesSelectedMonth(item.date));

    this.invoiceCount = this.filteredItems.length;
    this.totalAmount = this.filteredItems.reduce((sum, item) => sum + this.toAmount(item.final_price), 0);
    this.averageAmount = this.invoiceCount ? this.totalAmount / this.invoiceCount : 0;

    this.dailyBars = this.buildDailyBars();
    this.typeBars = this.buildTypeBars();
  }

  private buildDailyBars(): ChartBar[] {
    const [year, month] = this.monthYear.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const totals = new Map<string, { amount: number; count: number }>();

    for (let day = 1; day <= daysInMonth; day++) {
      totals.set(this.pad(day), { amount: 0, count: 0 });
    }

    this.filteredItems.forEach(item => {
      const date = new Date(item.date);
      if (isNaN(date.getTime())) return;
      const key = this.pad(date.getDate());
      const current = totals.get(key);
      if (!current) return;
      current.amount += this.toAmount(item.final_price);
      current.count += 1;
    });

    const maxAmount = Math.max(...Array.from(totals.values()).map(v => v.amount), 1);

    return Array.from(totals.entries()).map(([label, value]) => ({
      label,
      amount: value.amount,
      count: value.count,
      height: (value.amount / maxAmount) * 100
    }));
  }

  private buildTypeBars(): ChartBar[] {
    const totals = new Map<string, { amount: number; count: number }>();

    this.filteredItems.forEach(item => {
      const type = item.payment_type || 'Unknown';
      const current = totals.get(type) || { amount: 0, count: 0 };
      current.amount += this.toAmount(item.final_price);
      current.count += 1;
      totals.set(type, current);
    });

    const maxAmount = Math.max(...Array.from(totals.values()).map(v => v.amount), 1);

    return Array.from(totals.entries())
      .sort((a, b) => b[1].amount - a[1].amount)
      .map(([label, value]) => ({
        label,
        amount: value.amount,
        count: value.count,
        height: (value.amount / maxAmount) * 100
      }));
  }

  private matchesSelectedMonth(dateValue: any): boolean {
    if (!this.monthYear) return true;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return false;
    return this.toMonthValue(date) === this.monthYear;
  }

  private toMonthValue(date: Date): string {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}`;
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }

  private toAmount(value: any): number {
    const amount = Number(value);
    return isNaN(amount) ? 0 : amount;
  }
}
